/**
 * One persistence seam for the whole app.
 *
 * Online (a token is present) it reads and writes Postgres through the API,
 * mapping the server's snake_case columns onto the ledger's camelCase shape.
 * Offline — no token, or VITE_OFFLINE=true — it falls back to localStorage so
 * the UI can be worked on without a database running.
 *
 * Every screen goes through the load/sync functions here; nothing else in the
 * app talks to `api` or `localStorage` directly.
 */
import api, { hasSession } from './api.js';
import { parseDate } from './dates.js';
/* Reading a paged list to the end - see the note in that file for the lead
   that went missing when this was a bare limit of 1000. */
import { listAll } from './paging.js';

export const KEYS = {
  clients: 'client-ledger:v2',
  clientsLegacy: 'client-ledger:v1',
  settings: 'client-ledger:settings',
  crm: 'client-ledger:crm',
  alerts: 'client-ledger:alerts',
  marketing: 'client-ledger:mkt',
  brief: 'client-ledger:brief',
  digest: 'client-ledger:digest'
};

const FORCED_OFFLINE = String(import.meta.env.VITE_OFFLINE) === 'true';
const OFFLINE_CHOICE = 'rh_offline';

/** True when there is no server to talk to, so reads and writes stay local. */
export const isOffline = () => FORCED_OFFLINE || !hasSession();

/**
 * Whether running without a backend is an accepted state — either the build
 * forces it, or the user picked "explore offline" at the sign-in screen. This
 * is what the auth gate checks; `isOffline` is what the data layer checks.
 */
export const offlineAllowed = () =>
  FORCED_OFFLINE || localStorage.getItem(OFFLINE_CHOICE) === 'true';

export const chooseOffline = () => localStorage.setItem(OFFLINE_CHOICE, 'true');
export const clearOfflineChoice = () => localStorage.removeItem(OFFLINE_CHOICE);

/**
 * Server ids are UUIDs. Anything else was minted in the browser and has never
 * been written, which is how a create is told apart from an update.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/*
 * Ids minted in the browser look like `c-1757000000000-a1b2c` (see uid()).
 * Anything else already exists on a server: a UUID from our own database, or
 * the connected clients app's own key, such as `cl_143`.
 *
 * This used to test for a UUID alone, which judged every connector client to
 * be new. Edits were then POSTed as creates, the server rejected them, and
 * syncCollection swallowed the error - so "Contact logged", owner changes and
 * notes all reported success and never left the browser.
 */
const TEMP_ID = /^[a-z]{1,3}-\d{10,}-[a-z0-9]+$/i;
export const isServerId = id => !!String(id || '') && !TEMP_ID.test(String(id));
/** The same question the other way round, for callers that ask "still local?". */
const isLocalId = id => TEMP_ID.test(String(id || ''));
/* Foreign keys into our own tables are CHAR(36); a `cl_143` must not go there. */
const uuidOrNull = id => (UUID.test(String(id || '')) ? id : null);

/* ---------------------------------------------------------------- local */

const local = {
  get(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  set(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
  },
  remove(key) { try { localStorage.removeItem(key); } catch { /* ignore */ } }
};

/* ------------------------------------------------------ user directory */

/**
 * The ledger stores an owner as a name; the database stores a user id. This
 * directory is loaded once per session so the two can be translated in both
 * directions. A name with no matching user stays a plain name and simply does
 * not round-trip to `owner_id` — better than posting something the column
 * cannot hold.
 */
let usersById = new Map();
let usersByName = new Map();
/* The rows themselves, kept as well as the two lookups.
   The assign picker needs the whole person - the role decides whether they
   can be given work at all - and this call has already been made. Asking
   the server twice for the same list, once here and once in the store, is
   two answers that can disagree for as long as the second is in flight. */
let directory = [];

export const team = () => directory;

async function loadUsers() {
  if (isOffline()) return;
  try {
    const list = await api.auth.users();
    const people = Array.isArray(list) ? list : (list.data || []);
    directory = people;
    usersById = new Map(people.map(u => [u.id, u.name]));
    usersByName = new Map(people.map(u => [String(u.name).toLowerCase(), u.id]));
  } catch { /* not permitted to list users; owners stay as names */ }
}

/**
 * What to call the owner of a row.
 *
 * The id wins where there is a user behind it, because a user can be
 * renamed and the id follows; `owner_name` answers for the fifteen people
 * on the Owners list who do not have accounts. Falling back to the raw id
 * is the last resort and only ever shows up offline.
 */
const ownerOf = (id, name) => usersById.get(id) || name || id || '';
const ownerName = id => usersById.get(id) || id || '';
const ownerId = name => usersByName.get(String(name || '').toLowerCase()) || null;

/* -------------------------------------------------------------- mapping */

/*
 * Which of a partner's three codes somebody arrived on.
 *
 * Blank when there is no partner, or when the code they used is not one of
 * the three the account carries — which happens where a code has been
 * changed since they signed up, and is better said as nothing than guessed.
 */
function wayIn(c) {
  const used = String(c.partner_code_used || '').trim().toLowerCase();
  if (!used) return '';
  const is = v => String(v || '').trim().toLowerCase() === used;
  if (is(c.partner_referral_code)) return 'referral link';
  if (is(c.partner_promo_code)) return 'promo code';
  if (is(c.partner_tracking_code)) return 'tracking code';
  return '';
}

/** Server client row → ledger row. */
export function toLedgerRow(c) {
  return {
    id: c.id,
    sno: c.sno,
    name: c.name,
    email: c.email,
    phone: c.phone,
    contact: c.contact_person,
    /* The clients app's own label for the record. One value across all 1,227
       today, so it sorts and filters nothing yet; carried because it is the
       app's own word for the account and the day it starts varying it is
       already here rather than a round-trip away. */
    userTag: c.user_tag,
    acctType: c.acct_type,
    plan: c.plan,
    addons: (c.addons || []).join('; '),
    cycle: c.cycle,
    amount: c.amount,
    promo: c.promo,
    /*
     * Who brought them in, where a partner did.
     *
     * The clients app joins its own partner tables onto the account, so this
     * side never has to hold a second token or know that chain. Eight of
     * 1,348 accounts carry one today.
     *
     * `partnerVia` is the code they actually arrived on — a partner hands out
     * three (a referral link, a promo code, a tracking code) and which one
     * brought somebody in is the question they ask about their own channels.
     * `partnerWay` names which of the three it was, worked out here because
     * the answer is a comparison and every screen would otherwise repeat it.
     */
    partnerId: c.partner_id || null,
    partner: c.partner_name || '',
    partnerEmail: c.partner_email || '',
    /* The partner's whole set, the same on every account of theirs. Carried
       so the client panel can lay them out under their proper names rather
       than showing one code and leaving somebody to ask what the others are. */
    partnerReferral: c.partner_referral_code || '',
    partnerPromo: c.partner_promo_code || '',
    partnerTracking: c.partner_tracking_code || '',
    partnerVia: c.partner_code_used || '',
    partnerWay: wayIn(c),
    source: c.source,
    /* The label the row was imported under, carried across by convert.
       Blank on everything the connector sends - it is ours, not theirs. */
    segment: c.segment || '',
    status: c.status,
    pay: c.pay_status,
    regOn: c.reg_on,
    trialEnd: c.no_trial ? 'No trial' : c.trial_end,
    expiry: c.expiry,
    nextDue: c.next_due,
    lastPaid: c.last_paid,
    lastActivity: c.last_activity,
    lastContacted: c.last_contacted,
    handler: ownerName(c.owner_id),
    notes: c.notes,
    aiRisk: c.ai_risk,
    aiReason: c.ai_reason,
    dealId: c.deal_id,
    /*
     * How this account got onto the ledger.
     *
     * Empty for nearly everybody, because nearly everybody registered
     * themselves in the clients app. 'deal' and 'pipeline' are the ones
     * somebody here carried across, and they are the only two a manager
     * wants counted separately: who moved them, and from which board.
     */
    cameFrom: c.came_from || '',
    pays: c.pays || []
  };
}

/** Ledger row → the columns the clients endpoint accepts. */
/** A display date back to ISO, leaving anything unparseable alone. */
function isoOrRaw(v) {
  if (!v) return v;
  const d = parseDate(v);
  if (!d) return v;
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toServerClient(r) {
  const body = {
    name: r.name,
    email: r.email,
    phone: r.phone,
    contact_person: r.contact,
    acct_type: r.acctType,
    plan: r.plan,
    addons: String(r.addons || '').split(';').map(s => s.trim()).filter(Boolean),
    cycle: r.cycle,
    amount: r.amount,
    promo: r.promo,
    source: r.source,
    segment: r.segment || '',
    status: r.status,
    pay_status: r.pay,
    reg_on: r.regOn,
    trial_end: r.trialEnd,
    no_trial: String(r.trialEnd || '').trim().toLowerCase() === 'no trial',
    expiry: r.expiry,
    next_due: r.nextDue,
    last_paid: r.lastPaid,
    last_activity: r.lastActivity,
    // logContact() stamps a display string like "04 Sep 2026"; the clients
    // app formats its dates as 2026-09-04, so normalise before sending.
    last_contacted: isoOrRaw(r.lastContacted),
    notes: r.notes
  };
  if (r.sno) body.sno = r.sno;
  const owner = ownerId(r.handler);
  if (owner) body.owner_id = owner;
  // The connected clients app keys owner by name, not by a XYRA user id, so the
  // raw handler rides along too. The local `clients` table ignores this field.
  if (r.handler) body.owner = r.handler;
  // "No trial" is a flag, not a date the column can parse.
  if (body.no_trial) body.trial_end = '';
  return body;
}

/**
 * The tracker blob, however the driver handed it over.
 *
 * mysql2 parses a JSON column into an object; a database patched before the
 * column was JSON, or a row written by an older sync, gives back a string or
 * nothing. All three have to end as an object, because every reader of it
 * assumes one and a lead is not worth a blank card over a quoting detail.
 */
/* Exported for the page-journey window: a list response carries the intel
   without its events, so the window reads the one deal in full and parses
   the same way this file does. */
export function parseIntel(v) {
  if (!v) return null;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch { return null; }
}

const toDeal = d => ({
  id: d.id, title: d.title, cid: d.company_id, contact: d.contact_id, stage: d.stage,
  /* 'lead' until somebody promotes it, 'deal' after. Which board lists the
     row and which ladder of stages it climbs, nothing more. Read-only here:
     the promote route owns it, so an ordinary save cannot smuggle a lead onto
     the deal board. */
  kind: d.kind === 'lead' ? 'lead' : 'deal',
  value: Number(d.value), prob: d.probability, close: d.close_date,
  owner: ownerOf(d.owner_id, d.owner_name),
  source: d.source, segment: d.segment || '', plan: d.plan, cycle: d.cycle,
  next: d.next_step, notes: d.notes,
  aiScore: d.ai_score, aiWhy: d.ai_why, clientId: d.client_id,
  /* The address the row came in under. Read-only - the syncs own it - and
     carried here because it is how a deal is matched to the client it
     became when nobody ever wrote the link. See clientOfDeal(). */
  leadEmail: d.lead_email || '',
  /*
   * What the visitor tracker knew about them.
   *
   * `leadType` is which kind of form they filled in — Demo Request,
   * Enquiry/Quote, Contact, Chatbot, WhatsApp, Tawk — and `leadState` whether
   * they finished it: 'submitted', or 'partially_submitted' for a capture the
   * tracker never saw confirmed. Both are filtered on, so they are columns of
   * their own rather than a dig into the blob below.
   *
   * `intel` is the rest of the tracker's answer: source and UTM tags, landing
   * page and referrer, country and city, device and browser, their Google
   * profile, the visits they made and every form field they typed. Read-only —
   * trackerSync owns all three, and fromDeal() does not send them back.
   */
  leadType: d.lead_type || '',
  leadState: d.lead_state || '',
  intel: parseIntel(d.lead_intel),
  /* When the row arrived, which is what a date window on these pages asks
     about: a lead is interesting for when it came in, not when somebody
     guessed it might close. */
  created: d.created_at,
  /* Which sync wrote the row: 'xyra' for a signup, 'vizadtraq' for a demo
     request, empty when somebody typed it in. Read-only — the syncs own it. */
  origin: d.source_system || '',
  /*
   * They said no.
   *
   * A date, not a flag, because when they said it is half the answer: a lead
   * dropped in March is a different proposition from one dropped yesterday.
   * Read-only here — the drop route owns all four, and fromDeal() does not
   * send them back, so an ordinary save cannot quietly resurrect a row or
   * bury one.
   */
  dropped: d.dropped_at || null,
  dropReason: d.drop_reason || '',
  dropNote: d.drop_note || '',
  /* Which of the two buttons closed it: 'no' for Not interested,
     'quiet' for No response. The reason cannot say - both offer
     "Other" - and the report counts the two apart. */
  dropKind: d.drop_kind || '',
  droppedBy: d.dropped_by || null,
  /* Who first captured this lead, and how — set by the server and read-only.
     captureMethod is 'automatic' (a tracker/website/chatbot capture), 'single
     entry' (typed into the New-lead form) or 'bulk upload'; capturedById is the
     user for the latter two, resolved to a name where it is shown. */
  captureMethod: d.capture_method || '',
  capturedById: d.captured_by_user_id || null,
  capturedAt: d.captured_at || null,
  captureBatchId: d.capture_batch_id || null,
  /* The sub-status within the display stage (brief §6). Set by the Move-stage
     dialog; the pipeline reads it through subStatus() in domain/crm.js. */
  subStatus: d.sub_status || '',
  /* The workspace-unique lead number (shown as L-1024). Server-assigned. */
  leadNo: d.lead_no || null
});

const fromDeal = d => {
  const body = {
    title: d.title,
    /* Which page the row is on. Sent as-is: the promote route flips it on a
       win, and a row added by hand to the pipeline starts as a lead. */
    kind: d.kind === 'lead' ? 'lead' : 'deal',
    company_id: uuidOrNull(d.cid),
    contact_id: uuidOrNull(d.contact),
    stage: d.stage,
    value: d.value,
    close_date: d.close,
    source: d.source,
    segment: d.segment || '',
    plan: d.plan,
    cycle: d.cycle,
    next_step: d.next,
    notes: d.notes
  };
  if (d.prob !== '' && d.prob != null) body.probability = d.prob;
  // The deal-side half of the deal<->client link. Local column, so it survives
  // a connected clients app taking over the client read path.
  // Empty string rather than null to clear it: the resource factory turns an
  // empty *_id into SQL NULL, while null is coerced away and would silently
  // leave the old link in place.
  if (d.clientId !== undefined && !isLocalId(d.clientId)) body.client_id = d.clientId || '';
  /*
   * Both halves, always.
   *
   * owner_id is a user id and most of the people who chase this pipeline
   * are not users, so sending only the id threw the answer away: the lead
   * read as theirs until the next load and as nobody's after it. The name
   * goes with it — empty string to clear, since the column is nullable and
   * undefined would leave the old one in place.
   */
  body.owner_id = ownerId(d.owner) || '';
  body.owner_name = String(d.owner || '').trim();
  return body;
};

const toContact = c => ({
  created: c.created_at,
  id: c.id, cid: c.company_id, name: c.name, title: c.title, email: c.email,
  phone: c.phone, status: c.status, source: c.source, owner: ownerName(c.owner_id), notes: c.notes
});

const fromContact = c => {
  const body = {
    name: c.name, title: c.title, email: c.email, phone: c.phone,
    company_id: uuidOrNull(c.cid), status: c.status, source: c.source, notes: c.notes
  };
  const owner = ownerId(c.owner);
  if (owner) body.owner_id = owner;
  return body;
};

const toCompany = c => ({
  created: c.created_at,
  id: c.id, name: c.name, industry: c.industry, size: c.size, city: c.city,
  owner: ownerName(c.owner_id), web: c.website
});

const fromCompany = c => {
  const body = { name: c.name, industry: c.industry, size: c.size, city: c.city, website: c.web };
  const owner = ownerId(c.owner);
  if (owner) body.owner_id = owner;
  return body;
};

const toActivity = a => ({
  id: a.id, dealId: a.deal_id,
  /* Against an account rather than a deal: a trial being chased has no
     pipeline lead behind it, and the follow-up still has to live somewhere. */
  clientId: a.client_id,
  type: a.type, subject: a.subject,
  /* A person, or the app. Only a person's counts as having contacted them. */
  by: a.written_by || 'person',
  /*
   * Which board somebody was standing on when they wrote it: 'pipeline' or
   * 'deal'. The same record is a lead first and a deal afterwards, and the
   * log runs straight through the change — which is right, because it is one
   * relationship. But a call made while it was a lead and the same call made
   * after it became a deal are facts about different stages of the chase, and
   * the journey draws them as two runs because of this.
   *
   * Set by the server off the deal's own kind at the moment of writing, never
   * sent from here: a form opened on the pipeline and submitted after the row
   * was promoted would otherwise file a lead's note under the deal.
   */
  board: a.board || '',
  /*
   * When the row was written, to the second.
   *
   * `due` is a date and nothing finer, so everything logged today ties — and
   * the one question that needs the seconds is "did these come out of the
   * same form". The log form records a call and moves the stage in one
   * submission, which writes two or three rows within a second of each other;
   * a call made in the morning and a card moved in the afternoon are two
   * things somebody did. A day cannot tell those apart. This can.
   */
  at: a.created_at || null,
  due: a.due_on, done: a.done, who: ownerOf(a.owner_id, a.owner_name), notes: a.notes
});

const fromActivity = a => {
  const body = {
    subject: a.subject, type: a.type, deal_id: uuidOrNull(a.dealId),
    /* Not uuidOrNull: a client id comes from the clients app and is whatever
       that app calls it — "cl_1" is a real one. */
    client_id: String(a.clientId || '').trim() || null,
    written_by: a.by === 'system' ? 'system' : 'person',
    due_on: a.due, done: !!a.done, notes: a.notes
  };
  /* As on a deal: whoever did the work is usually not a user of this app. */
  body.owner_id = ownerId(a.who) || '';
  body.owner_name = String(a.who || '').trim();
  return body;
};

/* ----------------------------------------------------------------- read */

export async function loadSettings(defaults) {
  if (isOffline()) {
    const saved = local.get(KEYS.settings);
    if (!saved) return structuredClone(defaults);
    return {
      ...structuredClone(defaults),
      ...saved,
      prices: { ...defaults.prices, ...(saved.prices || {}) },
      modules: { ...defaults.modules, ...(saved.modules || {}) },
      /* No server offline, so no clients app - the plan price is the only
         amount the sample data has. Pinned rather than inherited from `saved`
         in case an older build stored the flag. */
      clientsApp: false
    };
  }
  await loadUsers();
  try {
    const { settings, org, clientsApp } = await api.settings.get();
    return {
      ...structuredClone(defaults),
      ...settings,
      /* Set after the spread so the live answer always beats a stale copy that
         an older build may have persisted into the org's settings blob. */
      clientsApp: !!clientsApp,
      /* Merged per key, not replaced - see settingsOf() on the server. */
      prices: { ...defaults.prices, ...(settings.prices || {}) },
      modules: { ...defaults.modules, ...(settings.modules || {}) },
      companyName: org.name ?? settings.companyName,
      currency: org.currency ?? settings.currency,
      trn: org.trn ?? settings.trn,
      city: org.city ?? settings.city
    };
  } catch {
    return structuredClone(defaults);
  }
}

export async function loadClients() {
  if (isOffline()) {
    for (const key of [KEYS.clients, KEYS.clientsLegacy]) {
      const saved = local.get(key);
      if (Array.isArray(saved) && saved.length) return saved;
    }
    return null;
  }
  try {
    const list = await api.clients.view();
    return Array.isArray(list) ? list.map(toLedgerRow) : null;
  } catch {
    return null;
  }
}

/** One statement line (invoice / payment / credit note) → camelCase. */
export function toStatementRow(t) {
  return {
    id: t.id,
    date: t.date,
    type: t.type,
    number: t.number,
    originalInvoice: t.original_invoice,
    description: t.description,
    dueDate: t.due_date,
    status: t.status,
    amountDisplay: t.amount_display,
    originalDisplay: t.original_display,
    runningDisplay: t.running_display
  };
}

/**
 * A client's statement of account for the detail view — invoices, payments and
 * credit notes with a running balance. Comes from the connected clients app.
 * Empty offline; otherwise a failure propagates so the caller can tell "nothing
 * connected / errored" apart from "connected, no transactions".
 */
export async function loadStatement(id) {
  if (isOffline()) return { items: [], available: false };
  const res = await api.clients.statement(id);
  const raw = Array.isArray(res) ? res : (res && (res.items || res.data)) || [];
  return { items: raw.map(toStatementRow), available: !(res && res.available === false) };
}

export async function loadCRM() {
  if (isOffline()) return local.get(KEYS.crm);
  try {
    /*
     * The database is the only source now.
     *
     * Tracked demo bookings used to be read through /leads and merged on top
     * of these rows, because nothing wrote them down. trackerSync and
     * accountSync now persist them as real deals, so merging the live read as
     * well showed every lead twice — 82 of 84 on the board the day the sync
     * landed. A synced row is also editable, which a read-through row never
     * was.
     */
    const [companies, contacts, deals, acts] = await Promise.all([
      listAll(api.companies),
      listAll(api.contacts),
      listAll(api.deals),
      listAll(api.activities)
    ]);

    return {
      companies: companies.map(toCompany),
      contacts: contacts.map(toContact),
      deals: deals.map(toDeal),
      acts: acts.map(toActivity)
    };
  } catch {
    return null;
  }
}

/**
 * Campaigns, audiences, templates and journeys.
 *
 * Online these live in the `marketing` table as one JSON document per org;
 * offline they fall back to localStorage. Everything else in this module
 * checks `isOffline()`; this one deliberately does not, because the answer is
 * the same either way. A local mirror is always kept for instant reloads.
 */
export async function loadMarketing() {
  if (isOffline()) return local.get(KEYS.marketing);
  try {
    const res = await api.marketing.get();
    return res && res.data !== undefined ? res.data : null;
  } catch {
    return null;
  }
}

export async function saveMarketing(m) {
  local.set(KEYS.marketing, m);
  if (isOffline()) return;
  try { await api.marketing.save(m); } catch { /* the next change retries */ }
}

/** Alert read/snooze/dismiss bookkeeping stays local — it is per person. */
export const loadAlertState = () => local.get(KEYS.alerts);
export const loadBrief = () => local.get(KEYS.brief);
export const loadDigest = () => local.get(KEYS.digest);

/* ---------------------------------------------------------------- write */

export async function saveSettings(settings) {
  /* clientsApp describes the deployment, not the org's preferences. It is
     answered fresh by the server on every load, so persisting it would only
     let a stale copy outlive the connector - and a stored `true` would follow
     the user into offline mode and blank out the sample data's amounts. */
  const { clientsApp, ...stored } = settings;
  local.set(KEYS.settings, stored);
  if (isOffline()) return;
  try {
    await api.settings.save({ name: stored.companyName, currency: stored.currency, settings: stored });
  } catch { /* keep the local copy; the next save retries */ }
}

/**
 * Push the changed records of one collection.
 *
 * A changed id with no matching row was deleted; a row whose id is not a UUID
 * has never been written, so it is created and its new server id reported back
 * for the caller to swap in. Failures are swallowed per record: one bad row
 * must not stop the rest, and the local copy is already saved.
 */
/*
 * Three at a time, not all of them at once.
 *
 * A rule that touches every unowned account marks twelve hundred rows
 * dirty in one go, and `Promise.all` over that list opened twelve
 * hundred fetches in one tick. A browser will only run six requests to
 * one origin at a time, so the other twelve hundred sat in its own
 * queue - and so did the next thing the person clicked. Pressing No
 * response on a lead did nothing at all for minutes, because its POST
 * was somewhere behind a background flush nobody had asked for.
 *
 * Three lanes leaves half the pool for whatever the person is actually
 * doing. A background push finishing a few seconds later costs nothing;
 * the screen locking up costs the click.
 */
const LANES = 3;

async function inLanes(items, work) {
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      await work(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(LANES, items.length) }, lane));
}

/*
 * What the server has already said no to, this session.
 *
 * Some of the book is not ours to write: a client that lives in the
 * XYRA app comes back 403 on every field the connector will not take,
 * and the row stays as the server has it, so the next sweep plans the
 * same change and sends it again. That is a loop with no exit, and it
 * was the loop filling the queue above. Refused once is refused until
 * the page is reloaded - by which time the connector may have changed,
 * which is the one thing that could make it worth another try.
 *
 * Not for 429: that is our own rate limiter saying "later", not the
 * server saying "never".
 */
const refused = new Set();
const isRefusal = e => e && e.status >= 400 && e.status < 500 && e.status !== 429;

async function syncCollection(resource, list, ids, toBody) {
  const byId = new Map(list.map(r => [r.id, r]));
  const remap = {};

  await inLanes(ids.filter(id => !refused.has(id)), async id => {
    const row = byId.get(id);

    if (!row) {
      if (isServerId(id)) await resource.remove(id).catch(() => {});
      return;
    }
    try {
      if (isServerId(id)) {
        await resource.update(id, toBody(row));
      } else {
        const created = await resource.create(toBody(row));
        if (created && created.id) remap[id] = created.id;
      }
    } catch (e) {
      /* Otherwise reported by the next load. */
      if (isRefusal(e)) refused.add(id);
    }
  });

  return remap;
}

/**
 * Clients are written locally on every change for instant feedback, then the
 * changed rows are pushed. Returns `{ localId: serverId }` for anything newly
 * created so the store can adopt the real ids.
 */
/**
 * Record a payment on the server, which writes the invoice locally and pushes
 * the roll-forward to the connected book. Returns null when offline so the
 * caller can fall back to the in-memory behaviour.
 */
export async function payClient(id, body) {
  if (isOffline() || !isServerId(id)) return null;
  return api.clients.pay(id, body);
}

export async function saveClients(rows, changedIds) {
  local.set(KEYS.clients, rows);
  if (isOffline() || !changedIds || !changedIds.length) return {};
  return syncCollection(api.clients, rows, changedIds, toServerClient);
}

/** The same, per pipeline collection. `changed` is `{ deals: [...], ... }`. */
export async function saveCRM(crm, changed) {
  local.set(KEYS.crm, crm);
  if (isOffline() || !changed) return {};

  /*
   * In order, not in parallel: a row can only carry a reference the server
   * understands once the thing it refers to has a server id.
   *
   * These four went at once, so a deal naming a company typed into the same
   * box was created alongside that company and sent its local id - which the
   * server dropped. A company is nobody's child, a contact belongs to a
   * company, a deal to both, and an activity to a deal, so that is the order.
   *
   * Each step rewrites the references the step before it just resolved, so
   * what goes out is right the first time rather than being corrected on the
   * next flush.
   */
  const at = (map, id) => (id && map[id]) || id;

  const companies = await syncCollection(api.companies, crm.companies, changed.companies || [], fromCompany);

  const contactRows = crm.contacts.map(c => ({ ...c, cid: at(companies, c.cid) }));
  const contacts = await syncCollection(api.contacts, contactRows, changed.contacts || [], fromContact);

  const dealRows = crm.deals.map(d => ({
    ...d, cid: at(companies, d.cid), contact: at(contacts, d.contact)
  }));
  const deals = await syncCollection(api.deals, dealRows, changed.deals || [], fromDeal);

  const actRows = crm.acts.map(a => ({ ...a, dealId: at(deals, a.dealId) }));
  const acts = await syncCollection(api.activities, actRows, changed.acts || [], fromActivity);

  return { companies, contacts, deals, acts };
}

export const saveAlertState = s => local.set(KEYS.alerts, s);
export const saveBrief = b => local.set(KEYS.brief, b);
export const saveDigest = d => local.set(KEYS.digest, d);

export function clearLocal() {
  Object.values(KEYS).forEach(local.remove);
}
